import cx from "classnames";
import isNull from "lodash/isNull";
import { memo, useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import {
  loadStripe,
  StripeExpressCheckoutElementReadyEvent,
} from "@stripe/stripe-js";

import s from "./StripeExpressPaymentForm.module.scss";
import { stripeKey } from "../../../firebase";
import StripeExpressPaymentFormInternal from "./StripeExpressPaymentFormInternal";
import { IonLabel } from "@ionic/react";
import { WLSkeleton } from "../../../../Common/components/WLSkeleton/WLSkeleton";
import useWebSignupStore from "../store";
import { getStripePriceValueWithPromo } from "../../../services/promo";
import { WLDivider } from "../../../../Common/components/WLDivider/WLDivider";
import { isMobile } from "../../../../Common/utils/common";
import { logWebSignupTimingOnce } from "../signupTiming";

const stripePromise = loadStripe(stripeKey);

type ExpressCheckoutStatus = "loading" | "available" | "unavailable";

const getAvailableExpressMethodKeys = (
  availablePaymentMethods: StripeExpressCheckoutElementReadyEvent["availablePaymentMethods"],
): string[] => {
  if (!availablePaymentMethods) {
    return [];
  }

  return Object.entries(availablePaymentMethods)
    .filter(([, enabled]) => enabled)
    .map(([method]) => method);
};

const StripeExpressPaymentForm = ({
  setRedirectToApp,
}: {
  setRedirectToApp: (data: boolean) => void;
}) => {
  const { selectedSubscription, validStripeCoupon } = useWebSignupStore();

  const [expressStatus, setExpressStatus] =
    useState<ExpressCheckoutStatus>("loading");

  // const getExpressCheckoutDescription = useCallback(() => {
  //   const expressCheckoutDescriptionVersion = new URLSearchParams(
  //     window.location.search
  //   ).get("expcdv");

  //   if (
  //     !isNull(expressCheckoutDescriptionVersion) &&
  //     !isEmpty(expressCheckoutDescriptionVersion)
  //   ) {
  //     if (expressCheckoutDescriptionVersion === "2") {
  //       return "Enter in seconds. No forms, no fuss, 100% secure.";
  //     } else {
  //       return "Simple, Secure checkout.";
  //     }
  //   } else {
  //     return "";
  //   }
  // }, []);

  const amount = useMemo(() => {
    if (
      selectedSubscription === undefined ||
      validStripeCoupon === undefined ||
      isNull(selectedSubscription)
    ) {
      return;
    }

    return !isNull(validStripeCoupon)
      ? Math.round(
        getStripePriceValueWithPromo(
          selectedSubscription.price,
          validStripeCoupon
        ) * 100
      ) // Convert to the smallest currency unit
      : Math.round(selectedSubscription.price * 100);
  }, [selectedSubscription, validStripeCoupon]);

  useEffect(() => {
    setExpressStatus("loading");
  }, [amount]);

  useEffect(() => {
    if (amount === undefined) {
      return;
    }

    logWebSignupTimingOnce(
      "express-form-amount-ready",
      "express form: amount ready (Stripe Elements can mount)",
      { amount },
    );
  }, [amount]);

  const onReady = ({
    availablePaymentMethods,
  }: StripeExpressCheckoutElementReadyEvent) => {
    const methods = getAvailableExpressMethodKeys(availablePaymentMethods);

    if (methods.length === 0) {
      setExpressStatus("unavailable");
      logWebSignupTimingOnce(
        "express-element-ready",
        "express form: Express Checkout ready (no wallets available)",
        { visible: false },
      );
      return;
    }

    setExpressStatus("available");
    logWebSignupTimingOnce(
      "express-element-ready",
      "express form: Express Checkout visible/ready",
      {
        visible: true,
        methods,
      },
    );
  };

  if (expressStatus === "unavailable") {
    return null;
  }

  const expressButtonHeight = isMobile() ? "48px" : "55px";

  return (
    <div className={s.container} id="wl-express-checkout-container">
      {expressStatus === "loading" && (
        <div className={s.loadingState}>
          <WLSkeleton width={isMobile() ? "70%" : "240px"} height="28px" />
          <WLSkeleton width={isMobile() ? "90%" : "360px"} height="20px" />
          <WLSkeleton width="100%" height={expressButtonHeight} />
        </div>
      )}

      {expressStatus === "available" && (
        <>
          <IonLabel
            id="express-checkout-label"
            className={cx(s.title, "win-header-5 medium winnersans primary-500")}
          >
            Express Checkout
          </IonLabel>
          <IonLabel
            id="express-checkout-subtitle-label"
            className={cx(
              "win-body",
              "medium gilroy",
              "light-dark",
              s.responsiveCheckoutDetailsLabel,
            )}
          >
            Enter in seconds. No forms, no fuss, 100% secure.
          </IonLabel>
        </>
      )}

      {amount !== undefined && (
        <div
          className={cx(s.elementsContainer, {
            [s.elementsContainerHidden]: expressStatus === "loading",
          })}
        >
          <Elements
            stripe={stripePromise}
            options={{
              locale: "en-AU",
              amount,
              currency: "aud",
              setup_future_usage: "off_session",
              mode: "subscription",
              payment_method_types: ["card", "link"],
            }}
          >
            <StripeExpressPaymentFormInternal
              onReady={onReady}
              setRedirectToApp={setRedirectToApp}
            />
          </Elements>

          {expressStatus === "available" && (
            <WLDivider text="or" hasPadding />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(StripeExpressPaymentForm);
